import { useState, useCallback, useEffect, useRef } from 'react';
import _ from 'lodash';

export interface MediaConfigState {
  slides: (string | null)[];
  loyaltyImage: string | null;
  ordersImage: string | null;
  profileImage: string | null;
  isLoading: boolean;
}

export enum MediaConfigKey {
  LOYALTY = 'loyaltyImage',
  ORDERS = 'ordersImage',
  PROFILE = 'profileImage'
}

interface UseMediaConfigProps {
  initialSlides?: string[];
  initialLoyalty?: string | null;
  initialOrders?: string | null;
  initialProfile?: string | null;
}

const MAX_SLIDES = 3;

const getFormattedSlides = (slides?: string[]): (string | null)[] => {
  if (!Array.isArray(slides)) {
    return [];
  }
  return slides
    .filter(slide => slide && slide.trim())
    .map(slide => slide.trim())
    .slice(0, MAX_SLIDES);
};

export const useMediaConfig = ({
  initialSlides = [],
  initialLoyalty = null,
  initialOrders = null,
  initialProfile = null
}: UseMediaConfigProps) => {
  const [mediaState, setMediaState] = useState<MediaConfigState>({
    slides: [],
    loyaltyImage: null,
    ordersImage: null,
    profileImage: null,
    isLoading: true
  });

  // Track if user has made local changes that shouldn't be overwritten by Firebase
  const hasLocalChanges = useRef(false);
  const prevProps = useRef({
    initialSlides,
    initialLoyalty,
    initialOrders,
    initialProfile
  });

  useEffect(() => {
    // Don't overwrite local changes with Firebase data
    if (hasLocalChanges.current && !mediaState.isLoading) {
      return;
    }

    const {
      initialSlides: prevSlides,
      initialLoyalty: prevLoyalty,
      initialOrders: prevOrders,
      initialProfile: prevProfile
    } = prevProps.current;

    const slidesChanged = !_.isEqual(prevSlides, initialSlides);
    const propsChanged =
      slidesChanged ||
      prevLoyalty !== initialLoyalty ||
      prevOrders !== initialOrders ||
      prevProfile !== initialProfile;

    if (propsChanged || mediaState.isLoading) {
      setMediaState(prev => ({
        ...prev,
        slides: getFormattedSlides(initialSlides),
        loyaltyImage: initialLoyalty,
        ordersImage: initialOrders,
        profileImage: initialProfile,
        isLoading: false
      }));

      prevProps.current = {
        initialSlides,
        initialLoyalty,
        initialOrders,
        initialProfile
      };
    }
  }, [initialSlides, initialLoyalty, initialOrders, initialProfile, mediaState.isLoading]);

  const setSlide = useCallback((index: number, image: string) => {
    if (index < 0 || index >= MAX_SLIDES) return;
    if (!image || !image.trim()) return;

    hasLocalChanges.current = true;
    setMediaState(prev => {
      const newSlides = [...prev.slides];
      while (newSlides.length < MAX_SLIDES) {
        newSlides.push(null);
      }
      if (newSlides.length > MAX_SLIDES) {
        newSlides.splice(MAX_SLIDES);
      }
      newSlides[index] = image.trim();
      return { ...prev, slides: newSlides };
    });
  }, []);

  const removeSlide = useCallback((index: number) => {
    if (index < 0 || index >= MAX_SLIDES) return;

    hasLocalChanges.current = true;
    setMediaState(prev => {
      const newSlides = [...prev.slides];
      while (newSlides.length <= index) {
        newSlides.push(null);
      }
      newSlides[index] = null;
      return { ...prev, slides: newSlides };
    });
  }, []);

  const updateImage = useCallback((key: MediaConfigKey) =>
    (image: string | null) => {
      hasLocalChanges.current = true;
      setMediaState(prev => ({ ...prev, [key]: image }));
    }, []);

  // Reset local changes flag to allow Firebase sync
  const resetLocalChanges = useCallback(() => {
    hasLocalChanges.current = false;
  }, []);

  return {
    mediaState,
    mediaActions: {
      setSlide,
      removeSlide,
      setLoyaltyImage: updateImage(MediaConfigKey.LOYALTY),
      setOrdersImage: updateImage(MediaConfigKey.ORDERS),
      setProfileImage: updateImage(MediaConfigKey.PROFILE),
      resetLocalChanges
    }
  };
};
